import React, { FC, useCallback, useEffect, useState } from 'react';
import { WebUxTitle } from '../WebUxContext';
import { Badge, Button, Segmented, Tag } from 'antd';
import { Account, AccountType } from '../../dto';
import { useConnection } from '../../connection/connection';
import { useBusinessContext } from '../../context/BusinessContext';
import { AccountTypeSection } from './AccountTypeSection';
import AccountCreateModal, {
  useAccountCreateModal,
} from './AccountCreateModal';
import { useEventListener, useEvents } from '../../common/EventContext';

type Props = {};

export const ChartOfAccountsPage: FC<Props> = (props) => {
  const [accountTypes, setAccountTypes] = useState<AccountType[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [category, setCategory] = useState<string>('Assets');
  const connection = useConnection();
  const business = useBusinessContext();

  const createModal = useAccountCreateModal();
  useEffect(() => {
    connection
      .get(`accounts/${business.businessId}/chart-of-accounts`)
      .then((res) => {
        setAccountTypes(res.data.accountTypes);
        setAccounts(res.data.accounts);
      });
  }, [business.businessId, connection]);
  useEventListener(
    'ACCOUNT',
    useCallback((evt) => {
      if (evt.action === 'CREATE') {
        setAccounts((prevState) => [...prevState, evt.data]);
      } else if (evt.action === 'UPDATE') {
        setAccounts((prevState) =>
          prevState.map((a) =>
            a.accountId === evt.data.accountId ? evt.data : a,
          ),
        );
      } else {
        setAccounts((prevState) =>
          prevState.filter((a) => a.accountId !== evt.data.accountId),
        );
      }
    }, []),
  );
  return (
    <div className="flex flex-col gap-2">
      <WebUxTitle>Chart of Accounts</WebUxTitle>
      <div className="flex flex-col gap-2">
        <div className="w-full flex justify-between items-center">
          <Segmented
            value={category}
            onChange={setCategory}
            size="large"
            options={[
              {
                value: 'Assets',
                label: (
                  <span className="flex gap-2 items-center">
                    Assets
                    <Badge
                      color="#BBB"
                      count={
                        accounts.filter((a) => a.accountCategory === 'Assets')
                          .length
                      }
                    />
                  </span>
                ),
              },
              {
                value: 'Liabilities',
                label: (
                  <span className="flex gap-2 items-center">
                    Liabilities & Credit Cards
                    <Badge
                      color="#BBB"
                      count={
                        accounts.filter(
                          (a) => a.accountCategory === 'Liabilities',
                        ).length
                      }
                    />
                  </span>
                ),
              },
              {
                value: 'Income',
                label: (
                  <span className="flex gap-2 items-center">
                    Income
                    <Badge
                      color="#BBB"
                      count={
                        accounts.filter((a) => a.accountCategory === 'Income')
                          .length
                      }
                    />
                  </span>
                ),
              },
              {
                value: 'Expenses',
                label: (
                  <span className="flex gap-2 items-center">
                    Expenses
                    <Badge
                      color="#BBB"
                      count={
                        accounts.filter((a) => a.accountCategory === 'Expenses')
                          .length
                      }
                    />
                  </span>
                ),
              },
              {
                value: 'Equity',
                label: (
                  <span className="flex gap-2 items-center">
                    Equity
                    <Badge
                      color="#BBB"
                      count={
                        accounts.filter((a) => a.accountCategory === 'Equity')
                          .length
                      }
                    />
                  </span>
                ),
              },
            ]}
          />
          <Button
            type="primary"
            onClick={() => {
              createModal.current?.create();
            }}
          >
            Add a new Account
          </Button>
        </div>
        <div className="flex flex-col">
          {accountTypes
            .filter((a) => a.accountCategory === category)
            .map((a) => (
              <AccountTypeSection
                key={a.accountTypeId}
                accountType={a}
                accounts={accounts.filter(
                  (ac) => ac.accountTypeId === a.accountTypeId,
                )}
                onAddNew={() => {
                  createModal.current?.create(a.accountTypeId);
                }}
                onEdit={(account) => {
                  createModal.current?.edit(account);
                }}
              />
            ))}
        </div>
      </div>
      <AccountCreateModal ref={createModal} accountTypes={accountTypes} />
    </div>
  );
};
